import { defineStore } from 'pinia';
import CreateMailoutApi from '@/api';
import type { GetProductsRequest, GetServiceCardRequest, Product, ServiceCardResponse } from '@/types/home';
import type { MyOrder } from '@/types/myOrders';
import { useTopbarStore } from '@/store/topbar';
import constant from '@/constant';
import config from '@/config';

export const useHomeStore = defineStore('home', {
  state: () => ({
    serviceCard: null as ServiceCardResponse | null,
    product: null as Product | null,
    myOrders: [] as MyOrder[],
    myOrdersError: null as string | null,
  }),

  actions: {
    async fetchServiceCard() {
      const topBarStore = useTopbarStore();
      try {
        const payload: GetServiceCardRequest = {
          market: topBarStore.topBar?.market || config.VUE_DEFAULT_MARKET,
        }
        this.serviceCard = await CreateMailoutApi.getPostCardDetails(payload)
        return this.serviceCard
      } catch (err: unknown) {
        console.error('HomeStore: error loading service card', err);
        throw err;
      }
    },
    async getProducts() {
      const topBarStore = useTopbarStore();

      try {
        const payload: GetProductsRequest = {
          country: topBarStore.topBar?.market || config.VUE_DEFAULT_MARKET,
          language: topBarStore.topBar?.language || config.VUE_DEFAULT_LANGUAGE,
          userType: topBarStore.topBar?.isBusinessUser ? 'business' : topBarStore.topBar?.isPrivateUser ? 'private' : 'public',
          recipientsCount: 0,
        };
        const productsResponse = await CreateMailoutApi.getProducts(payload);
        const productId = this.serviceCard?.id ?? constant.HOME.PRODUCT_ID;
        this.product =
          productsResponse.products.find((product) => product.productCode === productId) || null;
        return this.product;
      } catch (err: unknown) {
        console.error('HomeStore: error loading products', err);
        throw err;
      }
    },
    async fetchMyOrders() {
      this.myOrdersError = null;
      try {
        this.myOrders = await CreateMailoutApi.getMyOrders();
        return this.myOrders;
      } catch (err: unknown) {
        this.myOrdersError = err instanceof Error ? err.message : 'Failed to load orders';
        console.error('HomeStore: error loading my orders', err);
        throw err;
      }
    },
  },
});
